// Dashboard de desligamentos (a tela "Desligamentos" do Apps Script), agora com todo o histórico da
// planilha mãe (2022+) e todos os regimes. Agregação no front: são algumas centenas de registros.
// Gráficos com as peças/paleta validadas de components/estoque/graficos.js: série única em
// series[0]; a quebra voluntário/involuntário/não informado usa os 3 slots em ordem fixa, com
// legenda. Todo gráfico tem tooltip e visão em tabela.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ExternalLink } from "lucide-react";
import { getRhDesligamentos } from "../../api";
import { MESES, formatData } from "../estoque/comum";
import { CaixaTooltip, CardGrafico, Legenda, TabelaDados, eixoProps, temaGrafico } from "../estoque/graficos";
import { AvisoErro, inputStyle, palette, useIsMobile } from "../estoque/ui";
import { useFormatValor } from "./valores";
import RhLayout from "./RhLayout";

const TIPOS = ["Voluntário", "Involuntário", "Não informado"];
const ULTIMOS_12 = "12m";

/** Os últimos 12 meses (o atual inclusive), do mais antigo ao mais recente, como "aaaa-mm". */
export function ultimos12Meses(hoje = new Date()) {
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - 11 + i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
}
const FAIXAS = [
  { rotulo: "Até 3 meses", ate: 3 }, { rotulo: "3 a 6 meses", ate: 6 }, { rotulo: "6 a 12 meses", ate: 12 },
  { rotulo: "1 a 2 anos", ate: 24 }, { rotulo: "2 a 5 anos", ate: 60 }, { rotulo: "Mais de 5 anos", ate: Infinity },
];
const pct = (n, total) => (total ? `${Math.round((100 * n) / total)}%` : "—");

function contar(registros, chave, limite) {
  const contagem = {};
  registros.forEach((r) => { const k = chave(r) || "Não informado"; contagem[k] = (contagem[k] || 0) + 1; });
  const lista = Object.entries(contagem).map(([nome, quantidade]) => ({ nome, quantidade })).sort((a, b) => b.quantidade - a.quantidade);
  return limite ? lista.slice(0, limite) : lista;
}

function Kpi({ C, rotulo, valor, detalhe }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{rotulo}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>{valor}</div>
      {detalhe && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{detalhe}</div>}
    </div>
  );
}

/** Barras horizontais de uma série (categoria, motivo, departamento). */
function BarrasHorizontais({ C, T, titulo, subtitulo, dados, rotuloColuna, total }) {
  const altura = Math.max(120, dados.length * 28);
  return (
    <CardGrafico C={C} titulo={titulo} subtitulo={subtitulo} altura={altura}
      tabela={<TabelaDados C={C} linhas={dados} colunas={[
        { id: "nome", rotulo: rotuloColuna }, { id: "quantidade", rotulo: "Desligamentos", alinhar: "right" },
        { id: "pct", rotulo: "%", alinhar: "right", formato: (_, l) => pct(l.quantidade, total) },
      ]} />}>
      <ResponsiveContainer>
        <BarChart data={dados} layout="vertical" barCategoryGap={4} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid horizontal={false} stroke={T.grid} />
          <XAxis type="number" allowDecimals={false} {...eixoProps(T)} />
          <YAxis type="category" dataKey="nome" width={190} {...eixoProps(T)} tick={{ fill: T.tinta2, fontSize: 11 }} />
          <Tooltip cursor={{ fill: T.grid, opacity: 0.5 }} content={({ active, payload }) => active && payload?.length ? (
            <CaixaTooltip T={T} titulo={payload[0].payload.nome} linhas={[
              { rotulo: "Desligamentos", valor: `${payload[0].value} (${pct(payload[0].value, total)})`, cor: T.series[0] },
            ]} />) : null} />
          <Bar dataKey="quantidade" fill={T.series[0]} radius={[0, 4, 4, 0]} maxBarSize={18} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </CardGrafico>
  );
}

export default function DesligamentosView() {
  const formatBRL = useFormatValor();
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const T = temaGrafico(isLight);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [ano, setAno] = useState(String(new Date().getFullYear()));
  const [tipoContrato, setTipoContrato] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [tipo, setTipo] = useState("");

  useEffect(() => {
    getRhDesligamentos().then(setDados).catch((e) => setErro(e.message));
  }, []);

  const todos = useMemo(() => (dados?.registros || []).map((r) => ({
    ...r, ano: Number(r.data_desligamento.slice(0, 4)), mes: Number(r.data_desligamento.slice(5, 7)) - 1,
    tipo: r.tipo_desligamento || "Não informado",
  })), [dados]);
  const opcoes = (campo) => Array.from(new Set(todos.map((r) => r[campo]).filter(Boolean))).sort();
  const anos = useMemo(() => Array.from(new Set(todos.map((r) => r.ano))).sort((a, b) => b - a), [todos]);

  const meses12 = useMemo(() => ultimos12Meses(), []);
  const filtrados = useMemo(() => {
    const noPeriodo = (r) => (ano === ULTIMOS_12 ? meses12.includes(r.data_desligamento.slice(0, 7)) : !ano || r.ano === Number(ano));
    return todos.filter((r) =>
      noPeriodo(r) && (!tipoContrato || r.tipo_contratacao === tipoContrato) && (!empresa || r.empresa === empresa) && (!tipo || r.tipo === tipo));
  }, [todos, ano, meses12, tipoContrato, empresa, tipo]);
  const descricaoPeriodo = ano === ULTIMOS_12 ? "nos últimos 12 meses" : ano ? `em ${ano}` : "desde 2022";

  const total = filtrados.length;
  const porTipo = Object.fromEntries(TIPOS.map((t) => [t, filtrados.filter((r) => r.tipo === t).length]));
  const comMeses = filtrados.filter((r) => r.meses_de_casa !== null);
  const mediaMeses = comMeses.length ? comMeses.reduce((s, r) => s + r.meses_de_casa, 0) / comMeses.length : null;
  const valorRescisoes = filtrados.reduce((s, r) => s + Number(r.valor_rescisao || 0), 0);

  // Com um ano: os 12 meses; últimos 12 meses: mês/ano; com "todos os anos": um ponto por ano
  const serieTempo = useMemo(() => {
    const chaveDe = ano === ULTIMOS_12 ? (r) => r.data_desligamento.slice(0, 7) : ano ? (r) => r.mes : (r) => r.ano;
    const pontos = ano === ULTIMOS_12
      ? meses12.map((m) => ({ rotulo: `${MESES[Number(m.slice(5)) - 1].slice(0, 3)}/${m.slice(2, 4)}`, chave: m }))
      : ano ? MESES.map((m, i) => ({ rotulo: m.slice(0, 3), chave: i })) : [...anos].reverse().map((a) => ({ rotulo: String(a), chave: a }));
    return pontos.map((p) => {
      const doPonto = filtrados.filter((r) => chaveDe(r) === p.chave);
      return { rotulo: p.rotulo, ...Object.fromEntries(TIPOS.map((t) => [t, doPonto.filter((r) => r.tipo === t).length])), total: doPonto.length };
    });
  }, [filtrados, ano, anos, meses12]);

  const faixas = FAIXAS.map((f, i) => ({
    nome: f.rotulo,
    quantidade: comMeses.filter((r) => r.meses_de_casa < f.ate && (i === 0 || r.meses_de_casa >= FAIXAS[i - 1].ate)).length,
  }));
  const porCategoria = contar(filtrados, (r) => r.categoria);
  const porMotivo = contar(filtrados, (r) => r.motivo_desligamento, 10);
  const porDepartamento = contar(filtrados, (r) => r.unidade, 10);

  const select = (valor, setValor, rotuloTodos, itens) => (
    <select value={valor} onChange={(e) => setValor(e.target.value)} style={{ ...inputStyle(C), flex: "0 1 180px", width: "auto" }}>
      <option value="">{rotuloTodos}</option>
      {itens.map((i) => <option key={i} value={i}>{i}</option>)}
    </select>
  );
  const coresTipo = Object.fromEntries(TIPOS.map((t, i) => [t, T.series[i]]));
  const descricaoMeses = (m) => (m === null ? "—" : m < 12 ? `${Math.round(m)} meses` : `${(m / 12).toFixed(1).replace(".", ",")} anos`);

  return (
    <RhLayout titulo="Desligamentos" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <select value={ano} onChange={(e) => setAno(e.target.value)} style={{ ...inputStyle(C), flex: "0 1 180px", width: "auto" }}>
          <option value="">Todos os anos</option>
          <option value={ULTIMOS_12}>Últimos 12 meses</option>
          {anos.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        {select(tipo, setTipo, "Voluntários e involuntários", TIPOS)}
        {select(tipoContrato, setTipoContrato, "Todos os regimes", opcoes("tipo_contratacao"))}
        {select(empresa, setEmpresa, "Todas as empresas", opcoes("empresa"))}
      </div>

      {!dados ? (!erro && <div style={{ color: C.muted, fontSize: 13 }}>Carregando…</div>) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 150 : 190}px, 1fr))`, gap: 12, marginBottom: 16 }}>
            <Kpi C={C} rotulo="Desligamentos" valor={total} detalhe={descricaoPeriodo} />
            <Kpi C={C} rotulo="Voluntários" valor={pct(porTipo["Voluntário"], total)} detalhe={`${porTipo["Voluntário"]} pessoa(s)`} />
            <Kpi C={C} rotulo="Involuntários" valor={pct(porTipo["Involuntário"], total)} detalhe={`${porTipo["Involuntário"]} pessoa(s)`} />
            <Kpi C={C} rotulo="Tempo médio de casa" valor={descricaoMeses(mediaMeses)} />
            <Kpi C={C} rotulo="Rescisões pagas (PJ)" valor={formatBRL(valorRescisoes)} detalhe="registradas na plataforma" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 16, marginBottom: 16 }}>
            <div style={{ gridColumn: isMobile ? "auto" : "span 2" }}>
              <CardGrafico C={C} titulo={ano ? "Desligamentos por mês" : "Desligamentos por ano"} subtitulo="Voluntário × involuntário"
                tabela={<TabelaDados C={C} linhas={serieTempo} colunas={[
                  { id: "rotulo", rotulo: ano ? "Mês" : "Ano" }, ...TIPOS.map((t) => ({ id: t, rotulo: t, alinhar: "right" })),
                  { id: "total", rotulo: "Total", alinhar: "right" },
                ]} />}>
                <Legenda T={T} itens={TIPOS.map((t) => ({ rotulo: t, cor: coresTipo[t] }))} />
                <ResponsiveContainer height="88%">
                  <BarChart data={serieTempo} barCategoryGap="28%" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={T.grid} />
                    <XAxis dataKey="rotulo" {...eixoProps(T)} />
                    <YAxis allowDecimals={false} width={32} {...eixoProps(T)} />
                    <Tooltip cursor={{ fill: T.grid, opacity: 0.5 }} content={({ active, payload, label }) => active && payload?.length ? (
                      <CaixaTooltip T={T} titulo={`${label} · ${payload[0].payload.total} desligamento(s)`}
                        linhas={TIPOS.map((t) => ({ rotulo: t, valor: payload[0].payload[t], cor: coresTipo[t] }))} />) : null} />
                    {TIPOS.map((t, i) => (
                      <Bar key={t} dataKey={t} stackId="tipo" fill={coresTipo[t]} stroke={T.superficie} strokeWidth={1}
                        radius={i === TIPOS.length - 1 ? [4, 4, 0, 0] : 0} maxBarSize={28} isAnimationActive={false} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </CardGrafico>
            </div>
            <BarrasHorizontais C={C} T={T} titulo="Por categoria" dados={porCategoria} rotuloColuna="Categoria" total={total}
              subtitulo={dados.motivos_sem_categoria.length ? "“Sem categoria”: classifique em Motivos" : null} />
            <BarrasHorizontais C={C} T={T} titulo="Motivos mais frequentes" subtitulo="10 principais" dados={porMotivo} rotuloColuna="Motivo" total={total} />
            <CardGrafico C={C} titulo="Tempo de casa no desligamento" subtitulo="Desde a admissão no grupo"
              tabela={<TabelaDados C={C} linhas={faixas} colunas={[
                { id: "nome", rotulo: "Faixa" }, { id: "quantidade", rotulo: "Desligamentos", alinhar: "right" },
                { id: "pct", rotulo: "%", alinhar: "right", formato: (_, l) => pct(l.quantidade, comMeses.length) },
              ]} />}>
              <ResponsiveContainer>
                <BarChart data={faixas} barCategoryGap="28%" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={T.grid} />
                  <XAxis dataKey="nome" {...eixoProps(T)} interval={0} tick={{ fill: T.muda, fontSize: 10 }} />
                  <YAxis allowDecimals={false} width={32} {...eixoProps(T)} />
                  <Tooltip cursor={{ fill: T.grid, opacity: 0.5 }} content={({ active, payload, label }) => active && payload?.length ? (
                    <CaixaTooltip T={T} titulo={label} linhas={[
                      { rotulo: "Desligamentos", valor: `${payload[0].value} (${pct(payload[0].value, comMeses.length)})`, cor: T.series[0] },
                    ]} />) : null} />
                  <Bar dataKey="quantidade" fill={T.series[0]} radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </CardGrafico>
            <BarrasHorizontais C={C} T={T} titulo="Por departamento" subtitulo="10 principais" dados={porDepartamento} rotuloColuna="Departamento" total={total} />
          </div>

          <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 700 }}>Desligamentos {ano ? descricaoPeriodo : ""} ({total})</h3>
            <div style={{ maxHeight: 420, overflow: "auto" }}>
              <TabelaDados C={C} linhas={filtrados} colunas={[
                { id: "data_desligamento", rotulo: "Data", formato: (v) => formatData(v) },
                { id: "nome", rotulo: "Nome", formato: (v, l) => (
                  <button onClick={() => navigate(`/rh/colaboradores/${l.colaborador_id}`)} style={{ background: "none", border: "none", padding: 0, color: C.brand, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, textAlign: "left" }}>{v}</button>
                ) },
                { id: "tipo_contratacao", rotulo: "Regime" },
                { id: "empresa", rotulo: "Empresa", formato: (v) => v || "—" },
                { id: "meses_de_casa", rotulo: "Tempo de casa", alinhar: "right", formato: (v) => descricaoMeses(v) },
                { id: "tipo", rotulo: "Tipo" },
                { id: "motivo_desligamento", rotulo: "Motivo", formato: (v, l) => <>{v || "—"}<div style={{ color: C.muted, fontSize: 11 }}>{l.categoria}</div></> },
                { id: "termo_url", rotulo: "Termo", formato: (v) => (v ? <a href={v} target="_blank" rel="noreferrer" style={{ color: C.brand }}><ExternalLink size={13} /></a> : "") },
              ]} />
            </div>
          </section>
        </>
      )}
    </RhLayout>
  );
}
